/**
 * Kalkulationskette. Einzige Quelle der Wahrheit — der Client rechnet damit
 * die Live-Vorschau, der Worker rechnet beim Speichern damit neu. Client-
 * Summen werden nie uebernommen.
 *
 * Aufbau nach den Lieferungs- und Zahlungsbedingungen fuer Muehlenprodukte
 * (VDM, Fassung 01.04.2015), Abschnitt III: Preise je 100 kg netto,
 * einschliesslich Papiersack, frachtfrei Kaeuferstation.
 *
 *   Basispreis (frachtfrei, inkl. Sack)   EUR/100 kg
 * ± Verpackung        lose = Abschlag     EUR/100 kg
 * + Zuschlaege        CCC-frei, koscher…  EUR/100 kg
 * ± Fracht            nur bei "ab Werk"   EUR/100 kg
 * ─────────────────────────────────────────────────
 * = Bruttopreis                           EUR/100 kg
 * − Rabatt                                EUR/100 kg
 * ─────────────────────────────────────────────────
 * = Endpreis                              EUR/100 kg
 *
 *   Positionssumme = Endpreis × Menge_kg / 100
 *
 * Rundungsregel: jeder Summand wird EINZELN auf ganze Cent gerundet, und
 * beim Rabatt wird der BETRAG gerundet, nicht der rabattierte Preis. Nur so
 * addiert sich die gedruckte Spalte sichtbar auf und der Kunde kann das
 * Angebot mit dem Taschenrechner nachvollziehen.
 */

import { bpOf, e4ToCent, positionssumme, roundDiv } from "./money.js";

export interface ZuschlagInput {
  id: string | null;
  bezeichnung: string;
  betrag_e4: number;
}

export interface PositionInput {
  bezeichnung: string;
  verpackung_text: string;
  menge_kg: number;
  ust_bp: number;
  basispreis_e4: number;
  verpackung_e4: number;
  zuschlaege: ZuschlagInput[];
  /** Nur bei lieferbedingung 'ab_werk' gesetzt, dann negativ (Abschlag). */
  fracht_e4: number;
  rabatt_bp: number;
}

export interface ZuschlagErgebnis {
  id: string | null;
  bezeichnung: string;
  betrag_cent: number;
}

export interface PositionErgebnis {
  basispreis_cent: number;
  verpackung_cent: number;
  zuschlaege_cent: number;
  zuschlaege: ZuschlagErgebnis[];
  fracht_cent: number;
  bruttopreis_cent: number;
  rabatt_cent: number;
  endpreis_cent: number;
  positionssumme_cent: number;
  ust_bp: number;
}

export interface SteuerZeile {
  ust_bp: number;
  netto_cent: number;
  ust_cent: number;
}

export interface AngebotErgebnis {
  positionen: PositionErgebnis[];
  steuern: SteuerZeile[];
  summe_netto_cent: number;
  summe_ust_cent: number;
  summe_brutto_cent: number;
}

export function berechnePosition(p: PositionInput): PositionErgebnis {
  // Jeden Summanden einzeln runden — damit die gedruckte Spalte aufgeht.
  const basispreis_cent = e4ToCent(p.basispreis_e4);
  const verpackung_cent = e4ToCent(p.verpackung_e4);
  const fracht_cent = e4ToCent(p.fracht_e4);

  const zuschlaege = p.zuschlaege.map((z) => ({
    id: z.id,
    bezeichnung: z.bezeichnung,
    betrag_cent: e4ToCent(z.betrag_e4),
  }));
  const zuschlaege_cent = zuschlaege.reduce((s, z) => s + z.betrag_cent, 0);

  const bruttopreis_cent =
    basispreis_cent + verpackung_cent + zuschlaege_cent + fracht_cent;

  // Den Rabattbetrag runden, nicht den rabattierten Preis. Sonst steht auf
  // dem Beleg 42,40 − 3 % = 41,13, was der Kunde zu 1,27 zurueckrechnet,
  // waehrend intern 1,272 verrechnet wurde.
  const rabatt_cent = bpOf(bruttopreis_cent, p.rabatt_bp);
  const endpreis_cent = bruttopreis_cent - rabatt_cent;

  return {
    basispreis_cent,
    verpackung_cent,
    zuschlaege_cent,
    zuschlaege,
    fracht_cent,
    bruttopreis_cent,
    rabatt_cent,
    endpreis_cent,
    positionssumme_cent: positionssumme(endpreis_cent, p.menge_kg),
    ust_bp: p.ust_bp,
  };
}

export function berechneAngebot(positionen: PositionInput[]): AngebotErgebnis {
  const ergebnisse = positionen.map(berechnePosition);

  // USt je Steuersatz auf die Netto-Zwischensumme dieses Satzes — nicht je
  // Position aufsummiert. Muehlenerzeugnisse und Kleie 7 %, Palettenpfand 19 %.
  const jeSatz = new Map<number, number>();
  for (const e of ergebnisse) {
    jeSatz.set(e.ust_bp, (jeSatz.get(e.ust_bp) ?? 0) + e.positionssumme_cent);
  }

  const steuern: SteuerZeile[] = [...jeSatz.entries()]
    .sort(([a], [b]) => a - b)
    .map(([ust_bp, netto_cent]) => ({
      ust_bp,
      netto_cent,
      ust_cent: roundDiv(netto_cent * ust_bp, 10_000),
    }));

  // Summe der bereits gerundeten Positionssummen, nicht Runden der Rohsumme.
  const summe_netto_cent = ergebnisse.reduce((s, e) => s + e.positionssumme_cent, 0);
  const summe_ust_cent = steuern.reduce((s, z) => s + z.ust_cent, 0);

  return {
    positionen: ergebnisse,
    steuern,
    summe_netto_cent,
    summe_ust_cent,
    summe_brutto_cent: summe_netto_cent + summe_ust_cent,
  };
}
